// helpers/useLogin.ts
import { useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from './api/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface LoginCredentials {
    email: string;
    password: string;
}

const useLogin = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { loginUser } = useAuth();

    // Determine first route based on user type
    const findFirstPermittedRoute = (user: any) => {
        // Master users go to assistant
        if (user?.isMaster === true) {
            return '/assistant';
        }
        // All other users go to dashboard
        return '/dashboard';
    };

    const handleSubmit = async (
        event: React.FormEvent<HTMLFormElement> | null,
        keepLoggedIn: boolean,
        credentials?: LoginCredentials
    ) => {
        if (event) event.preventDefault();
        setError(null);
        const loginEmail = credentials?.email ?? email;
        const loginPassword = credentials?.password ?? password;
        if (!loginEmail || !loginPassword) {
            setError('Email and password are required.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await axios.post(
                ENDPOINTS.LOGIN,
                { email: loginEmail, password: loginPassword },
                { withCredentials: true }
            );
            const user = response.data;

            if (user?.accessToken) {
                localStorage.setItem('accessToken', user.accessToken);
                localStorage.setItem('oscar_access_token', user.accessToken);
            }
            if (user?.csrfToken) {
                localStorage.setItem('csrfToken', user.csrfToken);
            }
            
            // Fetch associated companies after successful login
            await new Promise(resolve => setTimeout(resolve, 1000));

            try {
                const companiesResponse = await axios.get(`${ENDPOINTS.GET_COMPANIES}`);
                const companies = companiesResponse.data;
                user.associatedCompanies = companies; // Update the user with associated companies
            } catch (companiesError) {
                console.warn('Failed to fetch companies, proceeding with login anyway:', companiesError);
                user.associatedCompanies = []; // Set empty array if companies fetch fails
            }
            
            // Handle "Keep Me Logged In" by setting the session duration
            if (keepLoggedIn) {
                document.cookie = `session_cookie=${response.data.token}; Max-Age=${60 * 60 * 24 * 30}; Path=/`; // 30 days
            } else {
                document.cookie = `session_cookie=${response.data.token}; Max-Age=${60 * 60 * 24}; Path=/`; // 1 day
            }
            
            loginUser(user);

            const pendingWhatsAppToken = localStorage.getItem('pendingWhatsAppVerifyToken');
            if (pendingWhatsAppToken) {
                localStorage.removeItem('pendingWhatsAppVerifyToken');
                navigate(`/whatsapp/verify-number?token=${encodeURIComponent(pendingWhatsAppToken)}`, { replace: true });
                return;
            }

            // Determine the first route user should go to after company selection
            const firstPermittedRoute = findFirstPermittedRoute(user);
            navigate('/select-company', { replace: true, state: { next: firstPermittedRoute } });
        } catch (error: any) {
            console.error('Login error', error);
            setError(error?.response?.data?.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return { email, setEmail, password, setPassword, error, isLoading, handleSubmit };
};

export default useLogin;
